/**
 * cssOverrides for the Dynamic widget.
 *
 * Dynamic exposes its theming primitives as `--dynamic-*` CSS variables.
 * Their default styles are scoped to `:root [data-dynamic-theme=...]`
 * (specificity 0,1,1) — to override we must match that selector form, not
 * just `:root`. We target every variant (`auto`, `dark`, `light`) so the
 * overrides apply regardless of which theme mode the SDK uses.
 *
 * Reference: https://docs.dynamic.xyz/design-customizations/css-overrides
 */

const TARGET = ':root [data-dynamic-theme]';

export const DYNAMIC_CSS_OVERRIDES = `
  ${TARGET} {
    /* surfaces — base ramp (1 = darkest, 4 = lightest used by Dynamic) */
    --dynamic-base-1: var(--bg-deep);
    --dynamic-base-2: var(--bg);
    --dynamic-base-3: var(--panel-2);
    --dynamic-base-4: var(--panel);

    /* brand */
    --dynamic-brand-primary-color: var(--cyan);
    --dynamic-brand-secondary-color: rgba(34, 211, 238, 0.15);
    --dynamic-brand-hover-color: var(--mint);

    /* connect button (the inline trigger before the modal opens) */
    --dynamic-connect-button-background: var(--bg-deep);
    --dynamic-connect-button-background-hover: rgba(34, 211, 238, 0.06);
    --dynamic-connect-button-color: var(--ink);
    --dynamic-connect-button-color-hover: var(--cyan);
    --dynamic-connect-button-border: 1px solid var(--line-bright);
    --dynamic-connect-button-border-hover: 1px solid var(--cyan);
    --dynamic-connect-button-shadow: none;
    --dynamic-connect-button-shadow-hover: 0 0 0 1px rgba(34, 211, 238, 0.2);

    /* buttons inside the auth / wallet modal */
    --dynamic-button-primary-background: var(--cyan);
    --dynamic-button-primary-hover: var(--mint);
    --dynamic-button-primary-border: 1px solid var(--cyan);
    --dynamic-button-primary-font-size: 11px;
    --dynamic-button-secondary-background: transparent;
    --dynamic-button-secondary-hover: rgba(34, 211, 238, 0.06);
    --dynamic-button-secondary-border: 1px solid var(--line-bright);
    --dynamic-button-secondary-font-size: 11px;
    --dynamic-button-shadow: none;

    /* text */
    --dynamic-text-primary: var(--ink);
    --dynamic-text-secondary: var(--ink-dim);
    --dynamic-text-tertiary: var(--ink-mute);
    --dynamic-text-link: var(--cyan);

    /* modal */
    --dynamic-modal-backdrop-background: rgba(4, 9, 15, 0.7);
    --dynamic-modal-backdrop-filter: blur(8px);
    --dynamic-modal-border: 1px solid var(--line-bright);

    /* search bar */
    --dynamic-search-bar-background: rgba(7, 17, 26, 0.5);
    --dynamic-search-bar-background-hover: rgba(7, 17, 26, 0.7);
    --dynamic-search-bar-background-focus: rgba(34, 211, 238, 0.06);
    --dynamic-search-bar-border: 1px solid var(--line);
    --dynamic-search-bar-border-hover: 1px solid var(--line-bright);
    --dynamic-search-bar-border-focus: 1px solid var(--cyan);

    /* wallet list tiles */
    --dynamic-wallet-list-tile-background: var(--panel-2);
    --dynamic-wallet-list-tile-background-hover: var(--panel);
    --dynamic-wallet-list-tile-shadow: none;
    --dynamic-wallet-list-tile-shadow-hover: 0 0 0 1px rgba(34, 211, 238, 0.18);
    --dynamic-wallet-list-tile-border: 1px solid var(--line);
    --dynamic-wallet-list-tile-border-hover: 1px solid var(--line-bright);

    /* footer */
    --dynamic-footer-background: transparent;
    --dynamic-footer-background-color: var(--panel-2);
    --dynamic-footer-text-color: var(--ink-dim);
    --dynamic-footer-border-top: 1px solid var(--line);
    --dynamic-footer-icon-color: var(--cyan);
    --dynamic-footer-arrow-color: var(--ink-mute);

    /* badges + pills */
    --dynamic-badge-background: var(--panel);
    --dynamic-badge-color: var(--ink-dim);
    --dynamic-badge-primary-background: rgba(110, 231, 183, 0.1);
    --dynamic-badge-primary-color: var(--mint);
    --dynamic-badge-dot-background: var(--mint);

    /* state colours */
    --dynamic-success-1: var(--mint);
    --dynamic-success-2: rgba(110, 231, 183, 0.1);
    --dynamic-error-1: var(--danger);
    --dynamic-error-2: rgba(249, 115, 115, 0.1);
    --dynamic-alert-1: var(--amber);
    --dynamic-alert-2: rgba(245, 196, 81, 0.1);

    /* tooltip */
    --dynamic-tooltip-color: var(--panel);
    --dynamic-tooltip-text-color: var(--ink);

    /* shadows + radius — sharp HUD edges */
    --dynamic-shadow-down-1: none;
    --dynamic-shadow-down-2: 0 8px 24px rgba(0, 0, 0, 0.4);
    --dynamic-shadow-down-3: 0 12px 40px rgba(0, 0, 0, 0.5);
    --dynamic-border-radius: 0;

    /* hover overlay */
    --dynamic-hover: rgba(34, 211, 238, 0.06);

    /* fonts */
    --dynamic-font-family-primary: 'JetBrains Mono', ui-monospace, monospace;
    --dynamic-font-family-numbers: 'JetBrains Mono', ui-monospace, monospace;
    --dynamic-text-size-button-primary: 11px;
    --dynamic-text-size-button-secondary: 10.5px;
    --dynamic-text-size-body-mini: 10px;
    --dynamic-text-size-body-small: 11px;
    --dynamic-text-size-body-normal: 12px;
    --dynamic-text-size-title: 13px;
  }

  /* extra non-variable polish — kill remaining roundness, force HUD letter
     spacing on the connect button. uses high-specificity selectors plus
     !important since these properties don't have first-class CSS variables. */
  ${TARGET} .dynamic-connect-button,
  ${TARGET} .dynamic-shadow-dom button,
  ${TARGET} button[class*='connect-button'] {
    text-transform: uppercase !important;
    letter-spacing: 0.14em !important;
    font-weight: 500 !important;
  }
`;
